<template>
	<div class="print-receipt font-sans text-sm text-black">
		<!-- Header: organizer name + receipt label -->
		<div class="flex items-start justify-between border-b border-black pb-4 mb-6">
			<div>
				<div class="text-base font-semibold">{{ customer.alias }}</div>
				<div v-if="customer.address">{{ customer.address }}</div>
				<div v-if="customer.zip || customer.city">{{ [customer.zip, customer.city].filter(Boolean).join(' ') }}</div>
				<div v-if="customer.vat">CVR: {{ customer.vat }}</div>
				<div v-if="customer.telephone">{{ customer.telephone }}</div>
				<div v-if="customer.email">{{ customer.email }}</div>
			</div>
			<div class="text-right">
				<div class="text-lg font-semibold">{{ $t('checkout.receipt') }}</div>
				<div class="text-xs text-gray-600">{{ $t('doorsale.receipt_title') }}</div>
			</div>
		</div>

		<!-- Order details -->
		<div class="mb-6 grid grid-cols-2 gap-x-8 gap-y-1">
			<div>
				<span class="text-gray-500">{{ $t('checkout.order_number') }}: </span>
				<span class="font-medium">{{ cart.receiptOrder.orderid }}</span>
			</div>
			<div v-if="orderTime">
				<span class="text-gray-500">{{ $t('checkout.order_time') }}: </span>
				<span class="font-medium">{{ orderTime }}</span>
			</div>
			<div v-if="transaction">
				<span class="text-gray-500">{{ $t('checkout.transaction_id') }}: </span>
				<span class="font-medium">{{ transaction }}</span>
			</div>
			<div v-if="cardnumber">
				<span class="text-gray-500">{{ $t('checkout.card_number') }}: </span>
				<span class="font-medium">{{ cardnumber.replace(/X/g, '*') }}</span>
			</div>
		</div>

		<!-- Buyer info -->
		<div
			v-if="info.name || info.email || info.telephone || info.address"
			class="mb-6"
		>
			<div class="font-semibold border-b border-gray-300 pb-1 mb-2">{{ $t('doorsale.print_buyer') }}</div>
			<div class="grid grid-cols-2 gap-x-8 gap-y-1">
				<div v-if="info.name">{{ info.name }}</div>
				<div v-if="info.email">{{ info.email }}</div>
				<div v-if="info.address || info.address2">
					{{ [info.address, info.address2].filter(Boolean).join(', ') }}
				</div>
				<div v-if="info.telephone">{{ info.telephone }}</div>
				<div v-if="info.zip || info.city">
					{{ [info.zip, info.city].filter(Boolean).join(' ') }}
				</div>
			</div>
		</div>

		<!-- Items -->
		<div class="mb-6">
			<div class="font-semibold border-b border-gray-300 pb-1 mb-2">{{ $t('doorsale.print_items') }}</div>
			<div v-for="item in cart.items" :key="item.id" class="mb-4">
				<!-- Event header -->
				<div class="font-semibold">{{ item.name }}</div>
				<div class="text-xs text-gray-500 mb-2">
					<span>{{ getDisplayDate(item, locale) }}</span>
					<span v-if="item.venueName"> · {{ item.venueName }}</span>
				</div>
				<!-- Ticket lines -->
				<table class="w-full text-xs">
					<tbody>
						<tr
							v-for="ticket in item.tickets.filter(t => t.count > 0)"
							:key="ticket.id"
							class="border-t border-gray-100"
						>
							<td class="py-1">{{ ticket.name1 }}</td>
							<td class="text-center py-1 w-10">{{ ticket.count }}</td>
							<td class="text-right py-1 w-20">
								{{ formatPrice(ticket.price, item.currency, locale) }}
							</td>
							<td class="text-right py-1 w-24 font-medium">
								{{ formatPrice(ticket.price * ticket.count, item.currency, locale) }}
							</td>
						</tr>
						<tr
							v-for="upsale in item.upsaleTickets.filter(t => t.count > 0)"
							:key="`u-${upsale.id}`"
							class="border-t border-gray-100"
						>
							<td class="py-1 pl-3 text-gray-600">{{ upsale.name1 }}</td>
							<td class="text-center py-1 w-10">{{ upsale.count }}</td>
							<td class="text-right py-1 w-20">
								{{ formatPrice(upsale.price, item.currency, locale) }}
							</td>
							<td class="text-right py-1 w-24 font-medium">
								{{ formatPrice(upsale.price * upsale.count, item.currency, locale) }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<!-- Financial summary -->
		<div class="mb-6">
			<div class="font-semibold border-b border-gray-300 pb-1 mb-2">{{ $t('doorsale.print_financial_summary') }}</div>
			<table class="w-full text-xs">
				<tbody>
					<tr v-if="totals.total_price > 0" class="border-t border-gray-100">
						<td class="py-1">{{ $t('doorsale.print_subtotal') }}</td>
						<td class="text-right py-1 w-28 font-medium">{{ formatPrice(totals.total_price, 'DKK', locale) }}</td>
					</tr>
					<tr v-if="totals.total_ticketfee > 0" class="border-t border-gray-100">
						<td class="py-1">{{ $t('common.fee') }}</td>
						<td class="text-right py-1 w-28">{{ formatPrice(totals.total_ticketfee, 'DKK', locale) }}</td>
					</tr>
					<tr v-if="totals.total_transaction_fee > 0" class="border-t border-gray-100">
						<td class="py-1">{{ $t('common.transaction_fee') }}</td>
						<td class="text-right py-1 w-28">{{ formatPrice(totals.total_transaction_fee, 'DKK', locale) }}</td>
					</tr>
					<tr v-if="cardFee > 0" class="border-t border-gray-100">
						<td class="py-1">{{ $t('doorsale.print_card_fee') }}</td>
						<td class="text-right py-1 w-28">{{ formatPrice(cardFee, 'DKK', locale) }}</td>
					</tr>
					<tr v-if="totals.total_discount > 0" class="border-t border-gray-100">
						<td class="py-1">{{ $t('common.discount') }}</td>
						<td class="text-right py-1 w-28">-{{ formatPrice(totals.total_discount, 'DKK', locale) }}</td>
					</tr>
					<tr v-if="dankortDiscount > 0" class="border-t border-gray-100">
						<td class="py-1">{{ $t('doorsale.print_dankort_discount') }}</td>
						<td class="text-right py-1 w-28">-{{ formatPrice(dankortDiscount, 'DKK', locale) }}</td>
					</tr>
					<tr
						v-for="gc in giftcards"
						:key="gc.code"
						class="border-t border-gray-100"
					>
						<td class="py-1">{{ $t('checkout.gift_card') }}: <span class="uppercase">{{ gc.code }}</span></td>
						<td class="text-right py-1 w-28">-{{ formatPrice(Math.abs(gc.balance), gc.currency, locale) }}</td>
					</tr>
					<tr class="border-t-2 border-black">
						<td class="py-2 font-semibold text-sm">{{ $t('common.total') }}</td>
						<td class="text-right py-2 w-28 font-semibold text-sm">{{ formatPrice(totals.total, 'DKK', locale) }}</td>
					</tr>
					<tr v-if="totals.total_vat > 0" class="border-t border-gray-100 text-gray-500">
						<td class="py-1">{{ $t('common.vat') }}</td>
						<td class="text-right py-1 w-28">{{ formatPrice(totals.total_vat, 'DKK', locale) }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Additional receipt message. The backend has already escaped or sanitized it, newlines are <br /> -->
		<div
			v-if="cart.receiptOrder.additional_receipt_message"
			class="mt-4 pt-4 border-t border-gray-300 text-xs text-gray-600 wrap-anywhere"
			v-html="cart.receiptOrder.additional_receipt_message"
		/>
	</div>
</template>

<script setup lang="ts">
	import { computed } from 'vue';
	import { useI18n } from 'vue-i18n';

	import { useCartStore } from '@/stores/cart';
	import { useGeneralStore } from '@/stores/general';
	import { getDisplayDate } from '@/utils/cartItemUtil';
	import { formatDateWithOffset } from '@/utils/formatDate';
	import { formatPrice } from '@/utils/formatPrice';

	const { locale } = useI18n();
	const cart = useCartStore();
	const generalStore = useGeneralStore();

	const customer = computed(() => generalStore.customer());
	const info = computed(() => cart.receiptOrder.info ?? {});
	const totals = computed(() => cart.cartTotals);
	const orderTime = computed(() => formatDateWithOffset(
		cart.receiptOrder.ts,
		cart.receiptOrder.ts_gmt_offset,
		{ locale: locale.value, showTime: true },
	));

	const giftcards = computed(() => {
		const fromPayment = cart.ReceiptConfirmPayment.giftcards;
		if (fromPayment?.length) return fromPayment;
		return cart.receiptOrder.giftcards ?? [];
	});

	const cardFee = computed(() => cart.ReceiptConfirmPayment.fee ?? 0);
	const dankortDiscount = computed(() => cart.receiptOrder.dankort_discount ?? 0);
	const transaction = computed(() => cart.ReceiptConfirmPayment.transaction || '');
	const cardnumber = computed(() => cart.ReceiptConfirmPayment.cardnumber || '');
</script>
